// Base API configuration
const API_BASE_URL = 'https://api.thrivecaremarket.co.uk/api';

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
};

// Helper function to handle response
const handleResponse = async (response: Response): Promise<any> => {
  // Handle 401 errors
  if (response.status === 401) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    
    throw new Error('Unauthorized');
  }

  // Handle network errors
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
};

// API client using fetch
const api = {
  get: async (url: string, config?: { params?: Record<string, any> }): Promise<any> => {
    const token = getAuthToken();
    const queryString = config?.params
      ? '?' + new URLSearchParams(config.params).toString()
      : '';
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  post: async (url: string, data?: any): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  patch: async (url: string, data?: any): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  delete: async (url: string): Promise<any> => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      return { data: await handleResponse(response) };
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        throw error;
      }
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },
};

export default api;

